if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.ensureRestockRocketButtonOnLoad();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );

            // Update media gallery for product swaps (combined listings)
            const currentMediaGallery = document.querySelector('.product__media-wrapper media-gallery');
            const newMediaGallery = html.querySelector('.product__media-wrapper media-gallery');
            if (currentMediaGallery && newMediaGallery) {
              HTMLUpdateUtility.viewTransition(
                currentMediaGallery,
                newMediaGallery,
                this.preProcessHtmlCallbacks,
                [() => {
                  // Re-initialize modal-openers and zoom after media gallery swap
                  if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
                  this.reinitializeModalOpeners();
                }]
              );
            }
          }

          // Update low stock text after product swap
          setTimeout(() => {
            this.updateVariantLowStockText();
          }, 100);

          // Dispatch event to notify other components that media gallery has been updated
          setTimeout(() => {
            document.dispatchEvent(new CustomEvent('product:media-gallery-updated'));
          }, 1000);

          // Re-render RestockRocket button for the new variant after product swap.
          // HTMLUpdateUtility.viewTransition inserts the new node synchronously and
          // renames the outgoing node's IDs, so the DOM is already correct here; we only
          // wait out its 500ms removal of the old node. App-load timing is handled by the
          // helper's readiness poll, so this no longer needs a long guessed delay.
          setTimeout(() => {
            this.renderRestockRocketButton(this.resolveBisVariantId(html.querySelector(selector)));
          }, 600);

          // Re-initialize OKE widgets after content update
          setTimeout(() => {
            if (window.okeWidgetApi && typeof window.okeWidgetApi.initAllWidgets === 'function') {
              window.okeWidgetApi.initAllWidgets();
            }
          }, 1250);
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();
        //Save last scroll position
        const lastScrollPosition = window.scrollY || window.pageYOffset;

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            //Restore scroll position
            window.scrollTo(0, lastScrollPosition);
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      // Resolve a valid variant id for the back-in-stock (RestockRocket) button.
      // Prefers the selected/first-available variant; falls back to the first option
      // value that resolves to a real variant, so the button still renders when the
      // selected variant is unavailable (e.g. a fully sold-out swapped-to product).
      resolveBisVariantId(scope) {
        if (!scope) return null;
        const selected = this.getSelectedVariant(scope);
        if (selected?.id) return selected.id;
        const resourceScripts = scope.querySelectorAll('script[data-resource]');
        for (const script of resourceScripts) {
          try {
            const variant = JSON.parse(script.textContent);
            if (variant?.id) return variant.id;
          } catch (e) {
            // Ignore malformed resource JSON and keep looking.
          }
        }
        return null;
      }

      // Render the RestockRocket button once the app is ready. Replaces the old blind
      // setTimeout so slow-loading app embeds no longer race the DOM update. Bounded
      // retry (~10 x 150ms) prevents an infinite loop if the app never loads.
      renderRestockRocketButton(variantId, attempts = 10) {
        if (!variantId) return;
        if (window._RestockRocket && typeof window._RestockRocket.renderButtonForVariant === 'function') {
          window._RestockRocket.renderButtonForVariant(variantId);
          return;
        }
        if (attempts <= 0) return;
        setTimeout(() => this.renderRestockRocketButton(variantId, attempts - 1), 150);
      }

      // Direct-load coverage: on page load of an unavailable variant, nudge the button.
      // Skipped for available variants (the app renders those itself) and no-ops when
      // no variant id can be resolved (e.g. single-variant products).
      ensureRestockRocketButtonOnLoad() {
        const variant = this.getSelectedVariant(this);
        if (variant && variant.available) return;
        this.renderRestockRocketButton(this.resolveBisVariantId(this));
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            // No available variant selected — still surface the notify button.
            this.renderRestockRocketButton(this.resolveBisVariantId(html));
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          const submitButton = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
          this.updateBuyButtonBadges(submitButton);
          const isDisabled = submitButton?.hasAttribute('disabled') ?? true;
          this.productForm?.toggleSubmitButton(
            isDisabled,
            isDisabled ? window.variantStrings.soldOut : undefined
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });

          // Update low stock text with a small delay to ensure price is updated
          setTimeout(() => {
            this.updateVariantLowStockText();
          }, 50);

          // Re-render RestockRocket button for the updated variant (waits for the app).
          setTimeout(() => {
            this.renderRestockRocketButton(variant?.id);
          }, 100);
        };
      }

      // The savings badge is priced per variant, so it must be replaced on every
      // variant change. toggleSubmitButton() only rewrites the button's label span,
      // which left the badge showing the previously selected variant's figure.
      // The correctly priced markup is already in the fetched section HTML, so we
      // copy it across rather than recomputing the discount in JS — recomputing
      // would duplicate the rules in snippets/price.liquid and eventually drift.
      updateBuyButtonBadges(sourceButton) {
        const targetButton = this.querySelector(`#ProductSubmitButton-${this.sectionId}`);
        if (!targetButton) return;

        targetButton.querySelectorAll('.button__badge').forEach((badge) => badge.remove());
        if (!sourceButton) return;

        // Badges belong between the label and the spinner, so the flex row reads
        // "Add to cart  SAVE $12" and the spinner stays last.
        const spinner = targetButton.querySelector('.loading__spinner');
        sourceButton.querySelectorAll('.button__badge').forEach((badge) => {
          const clone = badge.cloneNode(true);
          if (spinner) {
            targetButton.insertBefore(clone, spinner);
          } else {
            targetButton.appendChild(clone);
          }
        });
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      reinitializeModalOpeners() {
        // Re-attach click handlers to modal-opener buttons after DOM updates
        const modalOpeners = this.querySelectorAll('modal-opener');
        modalOpeners.forEach((opener) => {
          const button = opener.querySelector('button');
          if (!button) return;
          
          // Remove existing listeners by cloning (replaces element removing all listeners)
          const newButton = button.cloneNode(true);
          button.parentNode.replaceChild(newButton, button);
          
          // Re-attach the click handler
          newButton.addEventListener('click', () => {
            const modal = document.querySelector(opener.getAttribute('data-modal'));
            if (modal) modal.show(newButton);
          });
        });
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          
          // Re-initialize modal-opener button handlers after DOM manipulation
          this.reinitializeModalOpeners();
          
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        return fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
            if (selector === '.quantity__label') {
              const updatedAriaLabelledBy = updated.getAttribute('aria-labelledby');
              if (updatedAriaLabelledBy) {
                current.setAttribute('aria-labelledby', updatedAriaLabelledBy);
                // Update the referenced visually hidden element
                const labelId = updatedAriaLabelledBy;
                const currentHiddenLabel = document.getElementById(labelId);
                const updatedHiddenLabel = html.getElementById(labelId);
                if (currentHiddenLabel && updatedHiddenLabel) {
                  currentHiddenLabel.textContent = updatedHiddenLabel.textContent;
                }
              }
            }
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }

      updateVariantLowStockText() {
        // Get the low stock badge from the price component in the same section
        const priceElement = document.getElementById(`price-${this.dataset.section}`);
        const lowStockBadge = priceElement ? priceElement.querySelector('.price__badge--low-stock .hidden-infos') : null;
        const variantSelects = this.querySelector('variant-selects');
        const lowStockElements = variantSelects ? variantSelects.querySelectorAll('[data-variant-low-stock]') : [];
        
        if (priceElement?.querySelector('.price')?.classList.contains('price--low-stock') && lowStockBadge && lowStockElements.length > 0) {
          // Get the inner HTML from the low stock badge
          const lowStockText = lowStockBadge.innerHTML;
          
          // Update all variant low stock text elements
          lowStockElements.forEach(element => {
            element.innerHTML = lowStockText;
            element.style.display = 'inline';
          });
        } else {
          // Hide all low stock elements if no low stock badge is present
          lowStockElements.forEach(element => {
            element.style.display = 'none';
          });
        }
      }
    }
  );
}
